import { Shield, Scale, Copyright, AlertTriangle, Ban, Sparkles, FileText } from "lucide-react";
import { PageFooter } from "@/components/PageFooter";
import { useLang } from "@/i18n";

/**
 * Legal & Confidentiality page.
 * Aggregates the "Safe Content Frame", brand usage, IP/ownership, no-liability,
 * no-sharing and use-intent statements that protect the portal legally.
 */

const Section = ({ icon: Icon, eyebrow, title, children, testId }) => (
  <section
    className="bg-white border border-[#e8e8e8] rounded-sm p-6 sm:p-7"
    data-testid={testId}
  >
    <div className="flex items-start gap-4 mb-4">
      <div className="w-10 h-10 rounded-sm bg-[#000000] flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-white" strokeWidth={2} />
      </div>
      <div className="min-w-0">
        <p className="cchbc-eyebrow mb-1.5 text-[#dd260e]">{eyebrow}</p>
        <h2 className="font-condensed text-xl sm:text-2xl font-black uppercase tracking-tight text-[#000000]">
          {title}
        </h2>
      </div>
    </div>
    <div className="text-sm text-[#333333] leading-relaxed space-y-3 pl-0 sm:pl-14">
      {children}
    </div>
  </section>
);

export const Legal = () => {
  const { t } = useLang();
  return (
    <div data-testid="legal-page">
      {/* Header */}
      <div className="mb-8">
        <p className="cchbc-eyebrow mb-3 text-[#dd260e]">{t("About this platform · Legal")}</p>
        <h1 className="font-condensed text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-[#000000] mb-3">
          {t("Legal & Confidentiality")}
        </h1>
        <p className="text-sm text-[#555555] max-w-2xl">
          {t("Terms of use, intellectual-property notices and confidentiality framework for the FORA Onboard portal. Please read carefully — these terms apply to every page of this platform.")}
        </p>
      </div>

      <div className="space-y-5">
        {/* 4. SAFE CONTENT FRAME */}
        <Section
          icon={Shield}
          eyebrow={t("Section 01 · Use Frame")}
          title={t("Safe Content Frame")}
          testId="legal-safe-content-frame"
        >
          <p>
            {t("The information presented in this portal is intended solely for internal informational and enablement purposes within")}{" "}
            <strong className="text-[#000000]">Coca-Cola HBC</strong>.
          </p>
          <p>{t("It is not intended as a binding commercial offer, guarantee of performance, or contractual obligation.")}</p>
          <p>{t("All implementation examples and case studies are illustrative and may vary by market conditions.")}</p>
        </Section>

        {/* 9. USE INTENT STATEMENT */}
        <Section
          icon={Sparkles}
          eyebrow={t("Section 02 · Purpose")}
          title={t("Use Intent Statement")}
          testId="legal-use-intent"
        >
          <p>
            {t("This platform is designed to support")}{" "}
            <strong className="text-[#000000]">
              {t("internal decision-making, education, and capability building")}
            </strong>{" "}
            {t("across Coca-Cola HBC markets.")}
          </p>
          <p>{t("It does not replace formal commercial processes or agreements.")}</p>
        </Section>

        {/* 5. BRAND USAGE STATEMENT */}
        <Section
          icon={Scale}
          eyebrow={t("Section 03 · Brand Usage")}
          title={t("Coca-Cola & FORA Brand Usage")}
          testId="legal-brand-usage"
        >
          <p>
            {t("Coca-Cola trademarks and brand assets are the property of")}{" "}
            <strong className="text-[#000000]">{t("The Coca-Cola Company")}</strong>{" "}
            {t("and/or")}{" "}
            <strong className="text-[#000000]">Coca-Cola HBC</strong> {t("and are used within this platform under appropriate internal authorization.")}
          </p>
          <p>
            <strong className="text-[#000000]">{t("FORA by Master F&B")}</strong>{" "}
            {t("is an internal capability and solution framework used for demonstration and enablement purposes within Coca-Cola HBC markets.")}
          </p>
        </Section>

        {/* 6. OWNERSHIP / IP CLARITY */}
        <Section
          icon={Copyright}
          eyebrow={t("Section 04 · Intellectual Property")}
          title={t("Ownership Statement")}
          testId="legal-ownership"
        >
          <p>
            {t("All FORA methodologies, frameworks, and platform content are the intellectual property of")}{" "}
            <strong className="text-[#000000]">Master F&B</strong> {t("unless otherwise stated.")}
          </p>
          <p>{t("Coca-Cola brand assets remain the property of the Coca-Cola system.")}</p>
          <p>{t("Content is licensed for internal use only within Coca-Cola HBC and may not be reproduced or distributed externally.")}</p>
        </Section>

        {/* 7. NO LIABILITY / NO GUARANTEE */}
        <Section
          icon={AlertTriangle}
          eyebrow={t("Section 05 · No Guarantee")}
          title={t("No Liability / No Guarantee")}
          testId="legal-no-liability"
        >
          <p>
            {t("Any performance indicators, projections, or outcomes presented in this platform are based on")}{" "}
            <strong className="text-[#000000]">
              {t("internal benchmarks or case studies")}
            </strong>{" "}
            {t("and are")} <strong className="text-[#000000]">{t("not guaranteed")}</strong>{" "}
            {t("results.")}
          </p>
          <p>{t("Actual results may vary depending on market conditions, execution, and operational factors.")}</p>
        </Section>

        {/* 8. NO SHARING / NO DISTRIBUTION */}
        <Section
          icon={Ban}
          eyebrow={t("Section 06 · Confidentiality")}
          title={t("No Sharing / No Distribution")}
          testId="legal-no-sharing"
        >
          <p>
            {t("Users are")}{" "}
            <strong className="text-[#000000]">{t("strictly prohibited")}</strong> {t("from sharing, distributing, or reproducing any content outside authorized Coca-Cola HBC internal systems without prior written approval.")}
          </p>
          <p>{t("This includes — but is not limited to:")}</p>
          <ul className="space-y-1.5 pl-1">
            <li className="flex gap-2">
              <span className="text-[#dd260e] shrink-0">—</span>
              <span>{t("Forwarding materials by email or messaging apps.")}</span>
            </li>
            <li className="flex gap-2">
              <span className="text-[#dd260e] shrink-0">—</span>
              <span>{t("Exporting content (PDF, screenshots, recordings).")}</span>
            </li>
            <li className="flex gap-2">
              <span className="text-[#dd260e] shrink-0">—</span>
              <span>{t("Presenting any portal content externally.")}</span>
            </li>
          </ul>
          <p className="text-xs text-[#555555]">
            {t("Any unauthorized disclosure may result in disciplinary action.")}
          </p>
        </Section>

        {/* Footer note */}
        <div
          className="bg-[#fafafa] border border-[#e8e8e8] rounded-sm p-5 flex items-start gap-3"
          data-testid="legal-footer-note"
        >
          <FileText className="w-4 h-4 text-[#dd260e] mt-0.5 shrink-0" strokeWidth={2} />
          <p className="text-xs text-[#555555] leading-relaxed">
            <strong className="text-[#000000]">{t("FORA by Master F&B")}</strong>{" "}
            {t("— Confidential Internal Use Only. For questions about these terms or content licensing, contact")}{" "}
            <a
              href="mailto:pero.pehar@master-fb.com"
              className="text-[#dd260e] hover:underline"
            >
              pero.pehar@master-fb.com
            </a>{" "}
            (Master F&B) {t("or")}{" "}
            <a
              href="mailto:martina.lovetinska@cchellenic.com"
              className="text-[#dd260e] hover:underline"
            >
              martina.lovetinska@cchellenic.com
            </a>{" "}
            (Coca-Cola HBC).
          </p>
        </div>
      </div>

      <PageFooter />
    </div>
  );
};
